import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listFolderFor, type BulkItem } from '@/lib/bulk-selection';
import { listMessages, deleteMessage, bulkDeleteMessages } from '@/api/messages';
import { syncMailbox } from '@/api/sync';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { ShieldAlert, Paperclip, RefreshCw, Eye, Trash2, CheckSquare, Square, Search, X, Sparkles } from 'lucide-react';
import { useUi } from './ui-dialogs';
import { SenderAvatar } from './sender-avatar';

interface MessageListProps {
  mailboxId: string | null;
  selectedUid: number | null;
  /** IMAP folder of the open message: spam views list INBOX and Junk, where uids can repeat. */
  selectedImapFolder?: string | null;
  folder?: string;
  action?: 'clean' | 'review' | 'spam';
  onSweep?: () => void;
}

export function MessageList({
  mailboxId, selectedUid, selectedImapFolder, folder = 'inbox', action, onSweep }: MessageListProps) {
  const ui = useUi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectMode, setSelectMode] = useState(false);
  // Selected rows by cache row id: spam views list INBOX and Junk, where uids can repeat (B-03).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastShiftId, setLastShiftId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // PWA Phase 2: online/offline awareness for the cached-list banner.
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pullStartY = useRef<number | null>(null);
  const [pullDist, setPullDist] = useState(0);
  const [pulling, setPulling] = useState(false);

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  const queryKey = ['messages', mailboxId, folder, action, debouncedSearch];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => (mailboxId
      ? listMessages(mailboxId, { folder: listFolderFor(action), limit: 50, action, search: debouncedSearch || undefined })
      : Promise.resolve({ messages: [], pagination: { page: 1, limit: 50, total: 0 } })),
    enabled: !!mailboxId
  });

  const messages = data?.messages || [];

  const syncMutation = useMutation({
    mutationFn: () => (mailboxId ? syncMailbox(mailboxId) : Promise.resolve(null)),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', mailboxId] });
    }
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (items: BulkItem[]) => (mailboxId ? bulkDeleteMessages(mailboxId, items) : Promise.reject()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      setSelectedIds(new Set());
      setSelectMode(false);
    }
  });

  const folderLabel = folder === 'maybe-spam' ? 'Might be Spam' : folder === 'spam' ? 'Spam' : folder === 'sent' ? 'Sent' : folder === 'deleted' ? 'Deleted' : 'Inbox';

  const toggleSelect = (id: string, shiftKey: boolean = false) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      // Shift+click range selection
      if (shiftKey && lastShiftId !== null && lastShiftId !== id) {
        const ids = messages.map(m => m.id);
        const i1 = ids.indexOf(lastShiftId);
        const i2 = ids.indexOf(id);
        if (i1 >= 0 && i2 >= 0) {
          const lo = Math.min(i1, i2), hi = Math.max(i1, i2);
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          setLastShiftId(id);
          return next;
        }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      setLastShiftId(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === messages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map(m => m.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    if (!(await ui.confirm({
      title: `Delete ${n} message${n === 1 ? '' : 's'}?`,
      body: 'They move to Recently Deleted, where they can still be restored.',
      confirmLabel: `Delete ${n}`, danger: true,
    }))) return;
    bulkDeleteMutation.mutate(messages.filter(m => selectedIds.has(m.id)).map(m => ({ uid: m.uid, folder: m.folder || 'INBOX' })));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-bmail-border px-4 py-3">
        <h2 className="font-semibold text-bmail-text capitalize">
          {mailboxId ? folderLabel : 'Select a mailbox'}
        </h2>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
              className={cn(
                'rounded p-1.5 transition-colors',
                selectMode ? 'text-bmail-gold bg-bmail-surface-hover' : 'text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-gold'
              )}
              title="Multi-select"
            >
              <CheckSquare size={16} />
            </button>
          )}
          {selectMode && selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
              className="rounded p-1.5 text-bmail-error hover:bg-bmail-error/10 disabled:opacity-50"
              title={`Delete ${selectedIds.size} selected`}
            >
              <Trash2 size={16} />
            </button>
          )}
          {mailboxId && (
            <button
              title="Sweep"
              onClick={() => onSweep?.()}
              className="rounded p-1.5 text-bmail-text-secondary transition-colors hover:bg-bmail-surface-hover hover:text-bmail-gold"
            >
              <Sparkles size={16} />
            </button>
          )}
          {mailboxId && (
            <button
              title="Sync now"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="rounded p-1.5 text-bmail-text-secondary transition-colors hover:bg-bmail-surface-hover hover:text-bmail-gold disabled:opacity-50"
            >
              <RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />
            </button>
          )}
        </div>
      </div>

      {/* Search bar */}
      {mailboxId && (
        <div className="flex items-center gap-2 border-b border-bmail-border px-4 py-2">
          <Search size={14} className="shrink-0 text-bmail-text-secondary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages…"
            className="flex-1 bg-transparent text-sm text-bmail-text placeholder:text-bmail-text-secondary outline-none"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="shrink-0 rounded p-0.5 text-bmail-text-secondary hover:text-bmail-text"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Select-all bar */}
      {selectMode && messages.length > 0 && (
        <div className="flex items-center gap-2 border-b border-bmail-border bg-bmail-surface/50 px-4 py-1.5 text-xs text-bmail-text-secondary">
          <button onClick={selectAll} className="flex items-center gap-1 hover:text-bmail-gold">
            {selectedIds.size === messages.length ? <CheckSquare size={14} /> : <Square size={14} />}
            {selectedIds.size === messages.length ? 'Deselect all' : 'Select all'}
          </button>
          <span>{selectedIds.size} selected</span>
        </div>
      )}

      {error && (
        <div className="px-4 py-6 text-center text-sm text-bmail-error">
          Failed to load messages.
        </div>
      )}

      {!isLoading && messages.length === 0 && mailboxId && (
        <div className="px-4 py-12 text-center text-sm text-bmail-text-secondary">
          {folder === 'spam' && 'No spam detected. Nice!'}
          {folder === 'maybe-spam' && 'Nothing to review — inbox is clean.'}
          {folder === 'sent' && 'No sent messages.'}
          {folder === 'deleted' && 'No deleted messages.'}
          {folder === 'inbox' && 'No messages yet. Try syncing.'}
        </div>
      )}

      {!isLoading && !mailboxId && (
        <div className="px-4 py-12 text-center text-sm text-bmail-text-secondary">
          Select a mailbox from the sidebar.
        </div>
      )}

      {!online && (
        <div className="flex items-center justify-center gap-2 border-b border-bmail-border bg-bmail-surface px-4 py-2 text-xs text-bmail-text-secondary">
          <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
          Offline — showing last synced messages
        </div>
      )}

      {/* PWA Phase 2: pull-to-refresh (mobile). Drag down at the top of the list
          to trigger a mailbox sync; native-feeling spinner grows with the pull. */}
      {pullDist > 0 && (
        <div className="flex items-center justify-center overflow-hidden transition-none" style={{ height: pullDist }}>
          <span aria-hidden="true" className={cn('inline-block text-xl leading-none', pulling && 'bmail-buzz')} style={{ transform: `rotate(${pullDist * 4}deg)` }}>🐝</span>
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto"
        onTouchStart={(e) => {
          if (e.touches.length !== 1) return;
          const el = e.currentTarget;
          if (el.scrollTop <= 0 && !syncMutation.isPending) {
            pullStartY.current = e.touches[0].clientY;
          } else {
            pullStartY.current = null;
          }
        }}
        onTouchMove={(e) => {
          if (pullStartY.current === null) return;
          const dy = e.touches[0].clientY - pullStartY.current;
          setPullDist(dy > 0 ? Math.min(dy * 0.5, 64) : 0);
        }}
        onTouchEnd={() => {
          if (pullStartY.current !== null && pullDist >= 48) {
            setPulling(true);
            syncMutation.mutate(undefined, { onSettled: () => setPulling(false) });
          }
          pullStartY.current = null;
          setPullDist(0);
        }}
      >
        <div className="mx-auto max-w-3xl border-x border-bmail-border md:border-none">
        {messages.map((m) => {
          const isSelected = selectedIds.has(m.id);
          return (
            <div
              key={m.id}
              className={cn(
                'flex items-center border-b border-bmail-border px-2 transition-colors sm:px-3',
                selectedUid !== null && selectedUid === m.uid && mailboxId === m.mailboxId && (selectedImapFolder || 'INBOX') === (m.folder || 'INBOX') ? 'bg-bmail-surface-hover' : 'bg-transparent hover:bg-bmail-surface-hover',
                m.isRead ? 'font-normal text-bmail-text-secondary' : 'font-bold text-bmail-text',
                selectMode && isSelected && 'bg-bmail-goldfill/10'
              )}
            >
              {selectMode && (
                <button
                  onClick={(e) => toggleSelect(m.id, e.shiftKey)}
                  className="flex shrink-0 items-center px-2 text-bmail-text-secondary hover:text-bmail-gold"
                >
                  {isSelected ? <CheckSquare size={16} className="text-bmail-gold" /> : <Square size={16} />}
                </button>
              )}
              <SenderAvatar name={m.fromName} email={m.fromEmail} size={36} />
              <button
                onClick={() => navigate(`/inbox/${m.mailboxId}/${m.uid}?${new URLSearchParams({ ...(folder !== 'inbox' ? { folder } : {}), ...(m.folder ? { in: m.folder } : {}) }).toString()}`)}
                className="min-w-0 flex-1 px-3 py-3 text-left"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-[15px]">{m.fromName || m.fromEmail || 'Unknown'}</span>
                  <span className="shrink-0 text-xs text-bmail-text-secondary">{formatDate(m.date)}</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-[14px]">{m.subject || '(no subject)'}</span>
                  {m.spamAction === 'spam' && <ShieldAlert size={14} className="shrink-0 text-bmail-error" />}
                  {m.spamAction === 'review' && <Eye size={14} className="shrink-0 text-bmail-warning" />}
                  {m.hasAttachments && <Paperclip size={14} className="shrink-0 text-bmail-text-secondary" />}
                </div>
                <p className="mt-1 line-clamp-1 text-[13px] text-bmail-text-secondary">{m.snippet}</p>
              </button>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}